import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Button, useTheme } from "@mui/material";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const MediaTable = ({ pageId, token }) => {
  const [mediaData, setMediaData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  // Check dark mode from both theme and HTML class
  useEffect(() => {
    const checkDarkMode = () => {
      const hasDarkClass = document.documentElement.classList.contains('dark');
      const themeMode = theme.palette.mode === 'dark';
      setIsDarkMode(hasDarkClass || themeMode);
    };
    
    checkDarkMode();
    
    // Watch for changes to the dark class
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, [theme.palette.mode]);

  const handleConfigure = (postId) => {
    const qs = new URLSearchParams({ postId }).toString();
    navigate(`/dashboard/comments/configure?${qs}`);
  };

  const columns = [
    // { field: "id", headerName: "Media ID", width: 130 },
    {
      field: "media_url",
      headerName: "Post",
      width: 100,
      renderCell: (params) => {
        const { media_type, media_url, thumbnail_url } = params.row;

        let imageSrc;

        if (media_type === "VIDEO") {
          imageSrc = thumbnail_url || media_url || "/placeholder-image.png";
        } else if (media_type === "IMAGE" || media_type === "CAROUSEL_ALBUM") {
          imageSrc = media_url || "/placeholder-image.png";
        } else {
          imageSrc = media_url || thumbnail_url || "/placeholder-image.png";
        }

        return (
          <img
            src={imageSrc}
            alt="Media Preview"
            style={{ maxWidth: "100px", maxHeight: "50px", objectFit: "cover" }}
            onError={(e) => {
              e.target.onerror = null; // Prevent infinite loop
              e.target.src = "/placeholder-image.png";
            }}
          />
        );
      },
    },
    { field: "caption", headerName: "Description", width: 400 },
    {
      field: "status",
      headerName: "STATUS",
      width: 150,
      renderCell: (params) => {
        const isEnabled = params.row.autoCommentConfig?.isEnabled;

        return (
          <span
            onClick={() => handleConfigure(params.row.id)}
            style={{
              padding: "4px 8px",
              borderRadius: "8px",
              color: "white",
              backgroundColor: isEnabled ? "green" : "red",
              fontSize: "12px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {isEnabled ? "Configured" : "Not Configured"}
          </span>
        );
      },
    },
    {
      field: "totalMessagesSent",
      headerName: "Direct Messages Sent",
      width: 150,
    },
    {
      field: "totalAutoReplyComments",
      headerName: "Comments Sent",
      width: 180,
    },
    // { field: "sent", headerName: "Sent", width: 100 },
    // { field: "open", headerName: "Open", width: 100 },
    // { field: "clicks", headerName: "Clicks", width: 100 },
    // { field: "ctr", headerName: "CTR", width: 100 },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/media/message-count?pageId=${pageId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setMediaData(response.data);
      } catch (error) {
        console.error("Error fetching media data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (pageId && token) {
      fetchData();
    }
  }, [pageId, token]);

  return (
    <div style={{ height: 600, width: '100%' }}>
      {error && <p>Error: {error}</p>}
      <DataGrid
        rows={mediaData}
        columns={columns}
        loading={loading}
        initialState={{
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
        }}
          pageSizeOptions={[10, 20, 50]}
          getRowId={(row) => row.id}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: isDarkMode ? '#1f2937' : '#f9fafb',
              color: isDarkMode ? '#ffffff' : '#111827',
              fontWeight: 600,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
              color: isDarkMode ? '#ffffff' : '#111827',
            },
            '& .MuiDataGrid-cell': {
              color: isDarkMode ? '#ffffff' : '#111827',
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
            },
          }}
          components={{
            Toolbar: () => (
              <Button variant="contained" color="primary">
                {/* Add functionality for your desired action */}
                View Details
              </Button>
            ),
          }}
        />
    </div>
  );
};

export default MediaTable;
